import { Box, Flex, Heading, Text } from "@radix-ui/themes";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import {
	Locale,
	useLocalization,
} from "~/components/localization/LocalizationProvider";
import { OptimizedImage } from "~/components/OptimizedImage";
import { Link } from "~/components/RouterLink";
import type { LiveSeoContent as LiveSeoPageData } from "~/data/liveSeoContent.functions";
import styles from "../live-seo-page.module.css";

type SmileBestMedicineLocalization = {
	h1: string;
	date: string;
	byline: string;
	paragraphs: string[];
	linkPrefix: string;
	linkText: string;
	linkSuffix: string;
	relatedTitle: string;
	relatedDate: string;
	relatedPostTitle: string;
};

const smileBestMedicineLocalization = {
	[Locale.EN]: {
		h1: "Smile… It’s the Best Medicine",
		date: "May 25, 2017",
		byline: "By: Dr. Carmen Garcia-Paul",
		paragraphs: [
			"Everyone knows that your mouth is part of your body, but not everyone understands how much your oral health impacts your overall health.",
			"In a position paper published in the year 2000 entitled “Oral Health in America,” the U.S. Surgeon General highlighted the direct link between dental health and a person’s well-being. Since that time, oral infections, particularly periodontal issues or gum disease, have been shown to contribute to such conditions as heart disease, stroke, diabetes and pregnancy complications. Similarly, tooth decay, or bacterial infection of the teeth, can lead to serious medical complications that require emergency intervention if left untreated.",
			"Although it may not be the primary reason patients seek to straighten their teeth, orthodontic treatment goes a long way toward improving oral health. Crooked or crowded teeth often collect plaque and tartar more quickly than straight teeth, and they also are more difficult to clean. Straight teeth can therefore help patients maintain healthier gums and reduce the development of cavities. As explained by the Surgeon General, this improvement can in turn directly impact your overall health.",
			"The benefits of healthy, straight teeth go far beyond physical wellness. It’s also important to consider the emotional impact that a perfected smile can make on your daily life. Many people hide their teeth because they are embarrassed by the appearance of their smile. Straightened teeth can greatly increase self-confidence – directly impacting your ability to interact in social situations, develop strong relationships and excel in your career. Better still, recent research from the University of Kansas reveals that smiling can improve a person’s mood by releasing chemicals in the brain that decrease stress.",
			"So, whether it comes to your oral health, your physical health or your emotional well-being, smiling truly is the best medicine.",
		],
		linkPrefix: "Click ",
		linkText: "here",
		linkSuffix:
			" to see the article published in Miami’s Inspire Health magazine!",
		relatedTitle: "Also Worth Checking Out",
		relatedDate: "May 12, 2016",
		relatedPostTitle: "The Demolition is Real",
	},
	[Locale.ES]: {
		h1: "Sonríe… es la mejor medicina",
		date: "25 de mayo de 2017",
		byline: "Por: Dra. Carmen Garcia-Paul",
		paragraphs: [
			"Todos saben que la boca es parte del cuerpo, pero no todos entienden cuánto impacta la salud oral en la salud general.",
			"En un documento de posición publicado en el año 2000 titulado “Oral Health in America”, el Cirujano General de los Estados Unidos destacó el vínculo directo entre la salud dental y el bienestar de una persona. Desde entonces, se ha demostrado que las infecciones orales, especialmente los problemas periodontales o enfermedad de las encías, contribuyen a condiciones como enfermedades cardíacas, derrames cerebrales, diabetes y complicaciones durante el embarazo. De manera similar, la caries dental, o infección bacteriana de los dientes, puede causar complicaciones médicas graves que requieren intervención de emergencia si no se trata.",
			"Aunque quizá no sea la razón principal por la que los pacientes buscan enderezar sus dientes, el tratamiento de ortodoncia contribuye mucho a mejorar la salud oral. Los dientes torcidos o apiñados suelen acumular placa y sarro más rápido que los dientes rectos, y también son más difíciles de limpiar. Por eso, los dientes rectos pueden ayudar a los pacientes a mantener encías más saludables y reducir el desarrollo de caries. Como explicó el Cirujano General, esta mejora puede impactar directamente tu salud general.",
			"Los beneficios de tener dientes sanos y rectos van mucho más allá del bienestar físico. También es importante considerar el impacto emocional que una sonrisa perfeccionada puede tener en la vida diaria. Muchas personas esconden sus dientes porque sienten vergüenza por la apariencia de su sonrisa. Enderezar los dientes puede aumentar mucho la confianza en uno mismo, impactando directamente la capacidad de interactuar en situaciones sociales, desarrollar relaciones sólidas y sobresalir en la carrera profesional. Mejor aún, investigaciones recientes de la Universidad de Kansas revelan que sonreír puede mejorar el estado de ánimo al liberar sustancias químicas en el cerebro que disminuyen el estrés.",
			"Así que, ya sea por tu salud oral, tu salud física o tu bienestar emocional, sonreír realmente es la mejor medicina.",
		],
		linkPrefix: "Haz clic ",
		linkText: "aquí",
		linkSuffix:
			" para ver el artículo publicado en la revista Inspire Health de Miami.",
		relatedTitle: "También vale la pena revisar",
		relatedDate: "12 de mayo de 2016",
		relatedPostTitle: "La demolición es real",
	},
} satisfies Record<Locale, SmileBestMedicineLocalization>;

export function SmileBestMedicinePage({ page }: { page: LiveSeoPageData }) {
	const getLocalization = useLocalization(smileBestMedicineLocalization);
	const paragraphs = getLocalization("paragraphs");

	return (
		<Flex direction="column">
			<article className={styles.articleBand}>
				<Box className={styles.articleInner}>
					<OptimizedImage
						src="/smile-best-medicine.png"
						alt="Smiling woman with curly hair showcasing a bright smile, emphasizing the importance of oral health and confidence in orthodontic treatment, relevant to Miami orthodontics and braces."
						width={page.image?.width}
						height={page.image?.height}
						className={styles.featuredImage}
						fetchPriority="high"
						sizes="(max-width: 767px) 92vw, 760px"
					/>
					<Heading as="h1" className={styles.postTitle}>
						{getLocalization("h1")}
					</Heading>
					<Text as="p" className={styles.postDate}>
						{getLocalization("date")}
					</Text>
					<Box className={styles.entryContent}>
						<Heading as="h5" className={styles.byline}>
							{getLocalization("byline")}
						</Heading>
						{paragraphs.map((paragraph) => (
							<Text as="p" size="4" key={paragraph}>
								{paragraph}
							</Text>
						))}
						<Text as="p" size="4">
							{getLocalization("linkPrefix")}
							<a href="http://inspirehealthmag.com/smile-its-the-best-medicine/">
								{getLocalization("linkText")}
							</a>
							{getLocalization("linkSuffix")}
						</Text>
					</Box>
					<section className={styles.relatedPosts}>
						<Heading as="h3" className={styles.relatedTitle}>
							{getLocalization("relatedTitle")}
						</Heading>
						<Link to="/the-demolition-is-real" className={styles.relatedCard}>
							<OptimizedImage
								src="/miami-ortho-office-4.jpg"
								alt="Modern orthodontic office interior with green accents, showcasing dental equipment and seating, emphasizing Miami orthodontics and the welcoming environment for braces and Invisalign consultations."
								className={styles.relatedImage}
							/>
							<Box className={styles.relatedContent}>
								<Text as="p" className={styles.postDate}>
									{getLocalization("relatedDate")}
								</Text>
								<Heading as="h3" className={styles.relatedPostTitle}>
									{getLocalization("relatedPostTitle")}
								</Heading>
							</Box>
						</Link>
					</section>
				</Box>
			</article>
			<Box className={styles.formWrap}>
				<HomeConsultForm />
			</Box>
		</Flex>
	);
}
